import * as React from "react";
import Header from "../components/common/Header";
import BottomNav from "../components/common/BottomNav";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import axios from "axios";
import liff from "@line/liff";
import { ensureLiffInit } from "../liffClient";
import { colors, fontFamily } from "../theme/palette";

function App() {
  const [motherLanguage, setMotherLanguage] = React.useState("");
  const [studyingLanguage, setStudyingLanguage] = React.useState("");
  const [disabled, setDisabled] = React.useState(true);
  const [loggedIn, setLoggedIn] = React.useState(false);
  const [checking, setChecking] = React.useState(true);
  const [loginFailed, setLoginFailed] = React.useState(false);

  const handleLogin = async () => {
    const ok = await ensureLiffInit();
    if (!ok) {
      setLoginFailed(true);
      return;
    }
    liff.login();
  };

  const handleMotherLanguageChange = (event: SelectChangeEvent) => {
    setMotherLanguage(event.target.value as string);
    setDisabled(false);
  };
  const handleStudyingLanguageChange = (event: SelectChangeEvent) => {
    setStudyingLanguage(event.target.value as string);
    setDisabled(false);
  };

  const submitLanguages = async () => {
    setDisabled(true);
    await ensureLiffInit();
    const profile = await liff.getProfile();
    axios
      .post("v1/api/mypage/update", {
        motherLanguage: motherLanguage,
        studyingLanguage: studyingLanguage,
        userId: profile.userId,
      })
      .then(() => {
        setDisabled(false);
        alert("更新しました");
      });
  };

  React.useEffect(() => {
    const fetchMypage = async () => {
      const ready = await ensureLiffInit();
      const isLoggedIn = ready && liff.isLoggedIn();
      setLoggedIn(isLoggedIn);
      setChecking(false);
      if (!isLoggedIn) return;

      try {
        const profile = await liff.getProfile();
        const response = await axios.post("/v1/api/mypage/fetch", {
          userId: profile.userId,
        });

        setMotherLanguage(response.data.Items[0].mother_language);
        setStudyingLanguage(response.data.Items[0].studying_language);
      } catch (error) {
        console.error("Error set Languages:", error);
      }
    };

    fetchMypage();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.bg, display: "flex", flexDirection: "column" }}>
      <Box sx={{ width: "100%", position: "sticky", top: "0", zIndex: 100 }}>
        <Header />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, pt: 4, pb: 2, flex: 1, width: "100%" }}>
        {/* Page heading */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "13px",
              background: colors.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.4rem",
                color: colors.textPrimary,
                lineHeight: 1.2,
                fontFamily,
              }}
            >
              MyPage
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: colors.textMuted, fontFamily }}>
              アカウント設定
            </Typography>
          </Box>
        </Box>

        {!checking && !loggedIn && (
          <Box
            sx={{
              backgroundColor: colors.card,
              borderRadius: "18px",
              p: 3,
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography sx={{ color: colors.textSecondary, fontSize: "0.9rem", mb: 2, fontFamily }}>
              ログインするとアカウント設定を変更できます
            </Typography>
            <Button
              onClick={handleLogin}
              variant="contained"
              sx={{
                background: colors.accent,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontFamily,
                px: 3,
                "&:hover": { background: colors.accentDark },
              }}
            >
              LINEでログイン
            </Button>
            {loginFailed && (
              <Typography sx={{ color: colors.danger, fontSize: "0.78rem", mt: 1.5, fontFamily }}>
                ログインに接続できませんでした。時間をおいて再度お試しください。
              </Typography>
            )}
          </Box>
        )}

        {/* Language settings card */}
        {loggedIn && (
        <Box
          sx={{
            backgroundColor: colors.card,
            borderRadius: "18px",
            p: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
            border: `1px solid ${colors.border}`,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "0.9rem", color: colors.textLabel, mb: 2.5, fontFamily }}
          >
            言語設定
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel
              id="mother-language-id"
              shrink={Boolean(motherLanguage)}
              sx={{ fontSize: "0.9rem" }}
            >
              母国語 (Mother Tongue)
            </InputLabel>
            <Select
              labelId="mother-language-id"
              id="mother-language"
              value={motherLanguage}
              label="母国語 (Mother Tongue)"
              notched={Boolean(motherLanguage)}
              onChange={handleMotherLanguageChange}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value={"ja"}>日本語</MenuItem>
              <MenuItem value={"en"}>English</MenuItem>
              <MenuItem value={"fr"}>Français</MenuItem>
              <MenuItem value={"de"}>German</MenuItem>
              <MenuItem value={"es"}>Spanish</MenuItem>
              <MenuItem value={"it"}>Italian</MenuItem>
              <MenuItem value={"ko"}>Korean</MenuItem>
              <MenuItem value={"zh"}>Chinese</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel
              id="target-language-id"
              shrink={Boolean(studyingLanguage)}
              sx={{ fontSize: "0.9rem" }}
            >
              勉強中の言語 (Studying Language)
            </InputLabel>
            <Select
              labelId="target-language-id"
              id="target-language"
              value={studyingLanguage}
              label="勉強中の言語 (Studying Language)"
              notched={Boolean(studyingLanguage)}
              onChange={handleStudyingLanguageChange}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value={"en-US"}>English (American)</MenuItem>
              <MenuItem value={"en-GB"}>English (UK)</MenuItem>
              <MenuItem value={"en-AU"}>English (Australia)</MenuItem>
              <MenuItem value={"fr-FR"}>Français</MenuItem>
              <MenuItem value={"de-DE"}>German</MenuItem>
              <MenuItem value={"es-ES"}>Spanish</MenuItem>
              <MenuItem value={"it-IT"}>Italian</MenuItem>
              <MenuItem value={"ko-KR"}>Korean</MenuItem>
              <MenuItem value={"zh-CN"}>Chinese (China)</MenuItem>
              <MenuItem value={"ja-JP"}>日本語</MenuItem>
            </Select>
          </FormControl>

          <Button
            disabled={disabled}
            onClick={submitLanguages}
            fullWidth
            sx={{
              background: disabled ? undefined : colors.accent,
              color: disabled ? undefined : "#fff",
              borderRadius: "10px",
              py: 1.5,
              fontWeight: 600,
              fontSize: "0.95rem",
              textTransform: "none",
              fontFamily,
              "&:hover": {
                background: disabled ? undefined : colors.accentDark,
              },
            }}
          >
            更新する
          </Button>
        </Box>
        )}
      </Box>

      <BottomNav />
    </Box>
  );
}

export default App;
